import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MfaService } from "./mfa.service";
import { MessageService } from "../../../../../core/message/message.service";
import { AuthThrottleMessages } from "../const/data/auth-throttle-data";
import { MfaErrorType } from "../const/enums/mfa-error-type.enum";
import { MfaApiResponse, MfaSendCodeApiData } from "../const/models/mfa-api.models";

/** The body of a throttle response: an MFA result, a plain error result or a send-code result. */
type AuthThrottleResponseBody = Partial<MfaApiResponse> & Partial<MfaSendCodeApiData>;

const retryAfterHeader = "Retry-After";

/**
 * RYNO-14957: handles the three responses that refuse an authentication attempt without evaluating it, once
 * for every app, so the sign-in, password and phone-number services need a single early call.
 *
 * - 403 too_many_attempts: the code and the MFA token are gone; the code screen closes and the user is back
 *   at the step that issues a new code.
 * - 403 account_locked: the account stays locked for retry_after seconds, longer than the MFA token lives,
 *   so the code screen closes too.
 * - 429: a request rate limit; the screen stays and the user waits Retry-After seconds.
 *
 * The identity provider names the case in `error`; the OAuth server's lock is an OAuth error, invalid_grant
 * with account_locked in `error_description`; a refused resend carries the case in `error_result.error`.
 * Hence all three fields are read. The wait comes from `retry_after` in the body, else from the Retry-After
 * header, which the OAuth server sends on its own.
 */
@Injectable()
export class AuthThrottleService {
  constructor(
    private readonly messageService: MessageService,
    private readonly mfaService: MfaService
  ) {}

  /** Reports a throttle response to the user and returns its type, or null when the response is something else. */
  handle(response: HttpErrorResponse): MfaErrorType | null {
    const type = this.resolveType(response);

    switch (type) {
      case MfaErrorType.TooManyAttempts:
        this.closeCodeScreen();
        this.messageService.error(AuthThrottleMessages.tooManyAttempts);
        break;
      case MfaErrorType.AccountLocked:
        this.closeCodeScreen();
        this.messageService.error(
          AuthThrottleMessages.accountLocked(this.getRetryAfterSeconds(response))
        );
        break;
      case MfaErrorType.TooManyRequests:
        this.messageService.warning(
          AuthThrottleMessages.tooManyRequests(this.getRetryAfterSeconds(response)),
          "Just a moment"
        );
        break;
    }

    return type;
  }

  private resolveType(response: HttpErrorResponse): MfaErrorType | null {
    if (response?.status === 429) {
      return MfaErrorType.TooManyRequests;
    }
    if (response?.status !== 403) {
      return null;
    }

    const body = response.error as AuthThrottleResponseBody;
    const codes = [body?.error, body?.error_description, body?.error_result?.error];
    if (codes.includes(MfaErrorType.AccountLocked)) {
      return MfaErrorType.AccountLocked;
    }
    if (codes.includes(MfaErrorType.TooManyAttempts)) {
      return MfaErrorType.TooManyAttempts;
    }
    return null;
  }

  /** The code screen is open only while an MFA flow is in progress; closing it also clears that flow. */
  private closeCodeScreen(): void {
    if (this.mfaService.getMfaSessionData()) {
      this.mfaService.handleDefaultCloseAction();
    }
  }

  private getRetryAfterSeconds(response: HttpErrorResponse): number | undefined {
    const body = response.error as AuthThrottleResponseBody;
    if (typeof body?.retry_after === "number") {
      return body.retry_after;
    }

    const header = Number(response.headers?.get(retryAfterHeader));
    return header > 0 ? header : undefined;
  }
}
